import OT from "@opentok/client";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { useCallback, useEffect, useState } from "react";

import { useLocalTracks } from "hooks/liveMeeting/useLocalTracks";
import { useRoom } from "hooks/liveMeeting/useRoom";

import { selectAuth } from "store/auth/selectors";
import { useAppSelector } from "store/hooks";
import { upSertParticipantLeaveTime } from "store/participants/reducer";

export type Recording = {
  isRecording: boolean;
  connectionId: string | null;
};

// Key for sessionStorage
const SCREEN_SHARE_ACTIVE_KEY = "screenShareActive";

const usePublisher = (session: OT.Session | null | undefined) => {
  const [publisher, setPublisher] = useState<OT.Publisher | null>(null);
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoMuted, setVideoMuted] = useState(false);
  const dispatch = useDispatch();
  const guestName = localStorage.getItem("guestName") || "Guest";
  const [shareScreen, setShareScreen] = useState<{
    isSharing: boolean;
    publisher: OT.Publisher | null;
  }>({
    isSharing: false,
    publisher: null,
  });
  const [recording, setRecording] = useState<Recording>({
    isRecording: false,
    connectionId: null,
  });

  const auth = useAppSelector(selectAuth);
  const initialCamSettings = useAppSelector(
    (state) => state.meetings.initialCamSettings
  );

  const { localTracks, removeLocalAudioTrack, removeLocalVideoTrack } =
    useLocalTracks();

  const { room } = useRoom(localTracks, (error): any => {
    console.log(error);
    toast.error(error.message);
  });

  useEffect(() => {
    if (publisher && initialCamSettings) {
      publisher.publishAudio(initialCamSettings.hasAudio);
      publisher.publishVideo(initialCamSettings.hasVideo);
      setAudioMuted(!initialCamSettings.hasAudio);
      setVideoMuted(!initialCamSettings.hasVideo);
    }
  }, [initialCamSettings, publisher]);

  const initPublisher = useCallback(() => {
    if (session) {
      const newPublisher = OT.initPublisher(
        "publisher",
        {
          name: auth.user?.name || guestName,
          insertMode: "append",
          width: "100%",
          height: "100%",
          showControls: false,
          style: {
            backgroundImageURI: auth.user?.avatar_path || "",
          },
          echoCancellation: true, // Strongly recommended for audio quality
          autoGainControl: true, // Helps normalize audio levels
          noiseSuppression: true, // Reduces static and persistent background noise
          audioFallbackEnabled: true, // Prioritize audio quality over video if bandwidth is low
          videoContentHint: "detail", // For better video quality (e.g., screen sharing),
        },
        (error) => {
          if (error) {
            console.error("Error initializing main publisher:", error);
          }
        }
      );

      session.publish(newPublisher, (error) => {
        if (error) {
          console.error("Error publishing main stream", error);
        } else {
          console.log("Published main stream");
        }
      });
      localStorage.setItem("newPublisher", JSON.stringify(newPublisher));
      setPublisher(newPublisher);

      // Attempt to re-publish screen share if it was active before reload
      if (sessionStorage.getItem(SCREEN_SHARE_ACTIVE_KEY) === "true") {
        console.log("Attempting to re-publish screen share after reload.");
        // We'll call enableScreenShare after the main publisher is set
        // to ensure all necessary session connections are in place.
        // This will be handled in a separate effect or directly in this callback
        // if session.publish is synchronous enough. For now, let's keep it separate
        // to ensure the session is fully connected.
      }
    }
  }, [session, auth.user?.avatar_path]);

  useEffect(() => {
    initPublisher();
  }, [session, initPublisher]);

  // Effect to re-publish screen share after session and main publisher are ready
  useEffect(() => {
    if (
      session &&
      publisher &&
      sessionStorage.getItem(SCREEN_SHARE_ACTIVE_KEY) === "true"
    ) {
      console.log(
        "Re-publishing screen share as session and publisher are ready."
      );
      // Call enableScreenShare to re-init and publish the screen share
      // We need to make sure `enableScreenShare` can be called safely
      // even if `shareScreen.publisher` is not null (which it won't be on reload init)
      enableScreenShare();
      // Clear the flag after trying to re-enable
      sessionStorage.removeItem(SCREEN_SHARE_ACTIVE_KEY);
    }
  }, [session, publisher]); // Depend on session and publisher

  const handleCreatingShareScreenPublisherWrapper = useCallback(() => {
    const publisherWrapper = document.getElementById(
      "meeting-room-videos-area"
    );

    let screenPublisherElement = document.getElementById("screen-preview");
    if (!screenPublisherElement) {
      screenPublisherElement = document.createElement("div");
      screenPublisherElement.id = "screen-preview";
      screenPublisherElement.style.position = "absolute";
      publisherWrapper?.appendChild(screenPublisherElement);
    }
  }, []);

  const unpublishScreenShare = useCallback(() => {
    if (shareScreen.publisher) {
      session && session.unpublish(shareScreen.publisher);
      setShareScreen({
        isSharing: false,
        publisher: null,
      });
      sessionStorage.removeItem(SCREEN_SHARE_ACTIVE_KEY); // Update session storage
      // Remove the screen-preview div from the DOM
      const screenPreviewElement = document.getElementById("screen-preview");
      if (screenPreviewElement) {
        screenPreviewElement.remove();
      }
    }
  }, [session, shareScreen.publisher]); // Depend on shareScreen.publisher, not shareScreen object directly

  const enableScreenShare = useCallback(() => {
    // If we're already sharing, unpublish first. This handles toggling.
    if (shareScreen.isSharing) {
      unpublishScreenShare();
      return;
    }

    OT.checkScreenSharingCapability((response) => {
      if (!response.supported || response.extensionRegistered === false) {
        toast.error("This browser does not support screen sharing");
      } else if (response.extensionInstalled === false) {
        toast.error(
          "Please install the screen sharing extension and try again"
        );
      } else {
        handleCreatingShareScreenPublisherWrapper();
        const shareScreenPublisher = OT.initPublisher(
          "screen-preview",
          {
            videoSource: "screen",
            width: "100%",
            height: "100%",
            showControls: false,
            publishAudio: false,
          },
          (error) => {
            if (error) {
              console.error(
                "Error initializing screen share publisher:",
                error
              );
              setShareScreen({
                isSharing: false,
                publisher: null,
              });
              sessionStorage.removeItem(SCREEN_SHARE_ACTIVE_KEY);
              // Clean up the created div if publisher fails
              const screenPreviewElement =
                document.getElementById("screen-preview");
              if (screenPreviewElement) {
                screenPreviewElement.remove();
              }
            }
          }
        );

        session &&
          session.publish(shareScreenPublisher, (error) => {
            if (error) {
              console.error("Error publishing screen share stream:", error);
              setShareScreen({
                isSharing: false,
                publisher: null,
              });
              sessionStorage.removeItem(SCREEN_SHARE_ACTIVE_KEY);
              // Clean up the created div if publish fails
              const screenPreviewElement =
                document.getElementById("screen-preview");
              if (screenPreviewElement) {
                screenPreviewElement.remove();
              }
            } else {
              setShareScreen({
                isSharing: true,
                publisher: shareScreenPublisher,
              });
              sessionStorage.setItem(SCREEN_SHARE_ACTIVE_KEY, "true"); // Set flag
              console.log("Screen share published successfully.");
            }
          });

        shareScreenPublisher.on("destroyed", () => {
          console.log(
            "Screen share publisher destroyed by user action (e.g., browser stop sharing)."
          );
          setShareScreen({
            isSharing: false,
            publisher: null,
          });
          sessionStorage.removeItem(SCREEN_SHARE_ACTIVE_KEY); // Clear flag
          // Remove the screen-preview div from the DOM
          const screenPreviewElement =
            document.getElementById("screen-preview");
          if (screenPreviewElement) {
            screenPreviewElement.remove();
          }
        });
      }
    });
  }, [
    session,
    shareScreen.isSharing, // Only depend on isSharing to avoid infinite loop
    unpublishScreenShare,
    handleCreatingShareScreenPublisherWrapper,
  ]);

  // Use a separate effect to listen for mediaStopped on the screen share publisher
  useEffect(() => {
    if (shareScreen.publisher) {
      const handleMediaStopped = () => {
        console.log("Screen share media stopped event detected.");
        unpublishScreenShare();
      };
      shareScreen.publisher.on("mediaStopped", handleMediaStopped);

      return () => {
        shareScreen.publisher?.off("mediaStopped", handleMediaStopped);
      };
    }
  }, [shareScreen.publisher, unpublishScreenShare]);

  publisher &&
    publisher.on("streamDestroyed", (e) => {
      const now = new Date();
      if (publisher && e.stream.streamId === publisher.stream?.streamId) {
        setPublisher(null);
        dispatch(
          upSertParticipantLeaveTime({
            id: String(auth?.user?.id),
            leaveTime: now.toString(),
          })
        );
      }
    });

  const toggleAudio = useCallback(() => {
    if (publisher) {
      const muted = !audioMuted;
      publisher.publishAudio(!muted);
      setAudioMuted(muted);
    }
  }, [audioMuted, publisher]);

  const toggleVideo = useCallback(() => {
    if (publisher) {
      const muted = !videoMuted;
      publisher.publishVideo(!muted);

      setVideoMuted(muted);
    }
    if (window.localStream) {
      window.localStream!!.getTracks()[0].stop();
    }
    room?.localParticipant.tracks.forEach((publication) => {
      if (
        publication.track.kind === "audio" ||
        publication.track.kind === "video"
      ) {
        publication.track.stop();
        const attachedElements = publication.track.detach();
        attachedElements.forEach((element) => element.remove());
      }
    });
    room?.disconnect();
    removeLocalAudioTrack();
    removeLocalVideoTrack();
  }, [videoMuted, publisher]);

  return {
    publisher,
    toggleAudio,
    audioMuted,
    toggleVideo,
    videoMuted,
    enableScreenShare: shareScreen.isSharing
      ? unpublishScreenShare
      : enableScreenShare, // This will now correctly toggle
    screenShareEnabled: shareScreen.isSharing,
    isCurrUserScreenShare:
      shareScreen.publisher?.stream?.connection?.connectionId ===
      session?.connection?.connectionId,
    initPublisher,
    setShareScreen, // Keep this if you need external control, but internal management is better
    recording,
    setRecording,
  };
};

export default usePublisher;
